/**
 * The ONE door to a free JSON document inside a route contract.
 *
 * A response (or a request body) must be closed: every field is named, so a screen can never show something nobody
 * declared. Some payloads are documents on purpose (an audit snapshot, a stored preference blob). `jsonDocument`
 * is how a contract says so: it returns an ordinary `z.record(z.string(), z.unknown())` and registers THAT instance,
 * with the reason it is open. The closure check (`findOpenSchemas`) accepts only registered instances: a clone made
 * with `.describe()` or `.meta()` is another instance, so it is rejected. That is deliberate (it fails closed).
 *
 * In a REQUEST an unbounded document is an unbounded input: `defineRoute` accepts one in a body only when it declares
 * `maxBytes`, the most bytes the SERIALISED document (`JSON.stringify`, UTF-8; not the whole body) may take. Today the
 * cap is only DECLARED and no live route can carry one: `registerContractRoute` (backend) refuses to register a
 * contract whose body holds a free document, until the piece that brings the first one also brings the enforcement of
 * its cap. It can never exceed the largest body limit a route may declare (`MAX_ROUTE_BODY_LIMIT_BYTES`); the limit of
 * the route itself (`bodyLimitBytes`, `API_BODY_LIMIT_BYTES` by default) is checked by `defineRoute`.
 */

import { z } from 'zod/v4'

const MIN_REASON_LENGTH = 20

/**
 * The most bytes the API server accepts in a request body by default (10 MB; a route may raise it for itself, as the
 * custom import does with 50 MB, and declares it as `bodyLimitBytes`). The one source of that number: the servers
 * (`app.ts` and `platform/server/modes/api.ts`) take their `bodyLimit` from it, and `defineRoute` checks each document
 * of a body against the limit of its route.
 */
export const API_BODY_LIMIT_BYTES = 10_485_760

/** The largest body limit a route may declare: the custom import's 50 MB (`misc-routes.ts`). */
export const MAX_ROUTE_BODY_LIMIT_BYTES = 52_428_800

interface DocumentDeclaration {
  readonly reason: string
  readonly maxBytes: number | undefined
}

const documents = new WeakMap<z.ZodType, DocumentDeclaration>()

/**
 * A free JSON document, with the reason it is open (at least 20 characters once trimmed: «why is this not a schema?»)
 * and, for a request body, the most serialised bytes it may take (`maxBytes`, a positive integer up to
 * `MAX_ROUTE_BODY_LIMIT_BYTES`).
 */
export function jsonDocument(options: {
  reason: string
  maxBytes?: number
}): z.ZodRecord<z.ZodString, z.ZodUnknown> {
  const reason = options.reason.trim()
  if (reason.length < MIN_REASON_LENGTH) {
    throw new Error(
      `jsonDocument: reason must explain why the document is open (at least ${MIN_REASON_LENGTH} characters)`,
    )
  }
  const { maxBytes } = options
  if (maxBytes !== undefined && !(Number.isSafeInteger(maxBytes) && maxBytes >= 1)) {
    throw new Error('jsonDocument: maxBytes must be a positive integer')
  }
  if (maxBytes !== undefined && maxBytes > MAX_ROUTE_BODY_LIMIT_BYTES) {
    throw new Error(
      `jsonDocument: maxBytes cannot exceed the largest route body limit (${MAX_ROUTE_BODY_LIMIT_BYTES} bytes)`,
    )
  }
  const schema = z.record(z.string(), z.unknown())
  documents.set(schema, { reason, maxBytes })
  return schema
}

/** True only for the instance `jsonDocument` returned: a clone of it is not a document. */
export function isJsonDocument(schema: z.ZodType): boolean {
  return documents.has(schema)
}

/** The reason a document was declared open, or `undefined` when the schema is not a `jsonDocument`. */
export function jsonDocumentReason(schema: z.ZodType): string | undefined {
  return documents.get(schema)?.reason
}

/** The cap a document declared for a request, or `undefined` when it is unbounded (or not a `jsonDocument`). */
export function jsonDocumentMaxBytes(schema: z.ZodType): number | undefined {
  return documents.get(schema)?.maxBytes
}
